import { useState } from 'react';
import { Info, Lock, Save, CircleCheck as CheckCircle, MessageSquareText, Lightbulb, BookOpen, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import type { EAInputField, Classification } from '../../types/executiveAnalytics';

const CLASSIFICATION_COLORS: Record<Classification, string> = {
  verified: 'bg-app-positive/10 text-app-positive border-app-positive',
  derived: 'bg-app-focus/10 text-app-focus border-app-focus/30',
  modelled: 'bg-app-warning/10 text-app-warning border-app-warning',
  missing: 'bg-app-critical/10 text-app-critical border-app-critical',
};

const SOURCE_LABELS: Record<string, string> = {
  user_input: 'User Input',
  existing_heymakr_research: 'Heymakr intelligence',
  public_research: 'Public Research',
  uploaded_document: 'Uploaded Document',
  calculated: 'Calculated',
  inferred: 'Research estimate',
};

interface FieldCardProps {
  field: EAInputField;
  isEditing: boolean;
  editValue: string;
  onEdit: (id: string, val: string) => void;
  onSave: (f: EAInputField) => void;
  onValidateField?: (f: EAInputField) => void;
}

export default function FieldCard({
  field, isEditing, editValue, onEdit, onSave, onValidateField,
}: FieldCardProps) {
  const [showGuidance, setShowGuidance] = useState(false);
  const sourceLabel = SOURCE_LABELS[field.source_type] || field.source_type;
  const isPrefilled = field.source_type === 'existing_heymakr_research' || field.source_type === 'public_research';
  const isResearched = field.is_research_populated && field.source_type === 'inferred';
  const needsValidation = isResearched && !field.user_validated;
  const hasGuidance = field.description || field.why_it_matters || field.suggested_question || field.help_context;

  return (
    <div className={`bg-app-canvas rounded-lg p-3 border transition ${
      needsValidation
        ? 'border-app-warning ring-1 ring-app-warning/50'
        : isPrefilled && field.value
        ? 'border-app-focus/30'
        : isResearched && field.user_validated
        ? 'border-app-positive'
        : 'border-app-line'
    }`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-medium text-app-ink flex-1">{field.label}</span>
        {field.is_required && <span className="text-app-critical text-xs">*</span>}
        {!field.is_editable && <Lock className="w-3 h-3 text-app-muted" />}
        {hasGuidance && (
          <button
            onClick={() => setShowGuidance(v => !v)}
            className={`p-0.5 rounded transition-colors ${showGuidance ? 'bg-app-focus/10 text-app-focus' : 'hover:bg-app-line text-app-muted'}`}
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        {isResearched && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-app-focus/10 text-app-focus border border-app-focus/30">
            Research estimate
          </span>
        )}
        {isPrefilled && field.value && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-app-focus/10 text-app-focus border border-app-focus/30">
            {sourceLabel}
          </span>
        )}
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${CLASSIFICATION_COLORS[field.classification]}`}>
          {field.classification}
        </span>
        {field.confidence_score > 0 && (
          <span className="text-[10px] text-app-muted">{Math.round(field.confidence_score * 100)}%</span>
        )}
        {needsValidation && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-app-warning/10 text-app-warning border border-app-warning">
            Needs review
          </span>
        )}
        {isResearched && field.user_validated && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-app-positive/10 text-app-positive border border-app-positive flex items-center gap-0.5">
            <CheckCircle className="w-2.5 h-2.5" />
            Accepted
          </span>
        )}
      </div>

      {showGuidance && (
        <GuidancePanel field={field} />
      )}

      {isEditing && field.is_editable ? (
        <div className="flex gap-2">
          <input
            type="text"
            value={editValue}
            onChange={e => onEdit(field.id, e.target.value)}
            className="flex-1 px-3 py-2 border border-app-line-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-app-focus"
            autoFocus
          />
          <button onClick={() => onSave(field)} className="p-2 bg-app-brand text-white rounded-lg hover:bg-app-brand transition-colors">
            <Save className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div
            onClick={() => field.is_editable && onEdit(field.id, field.value || '')}
            className={`flex-1 px-3 py-2 bg-app-surface border border-app-line rounded-lg text-sm ${field.is_editable ? 'cursor-pointer hover:border-app-brand/20' : 'cursor-not-allowed opacity-60'}`}
          >
            {field.value || <span className="text-app-muted italic">Not set</span>}
          </div>
          {needsValidation && onValidateField && (
            <button
              onClick={() => onValidateField(field)}
              className="flex items-center gap-1 px-2.5 py-2 bg-app-positive/10 text-app-positive border border-app-positive rounded-lg text-xs font-medium hover:bg-app-positive/10 transition-colors flex-shrink-0"
              title="Accept this value"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              Accept
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function GuidancePanel({ field }: { field: EAInputField }) {
  const [expandedSection, setExpandedSection] = useState<string | null>('description');

  const sections = [
    { key: 'description', label: 'What is this?', icon: BookOpen, content: field.description, color: 'text-app-focus' },
    { key: 'why_it_matters', label: 'Why it matters', icon: Lightbulb, content: field.why_it_matters, color: 'text-app-warning' },
    { key: 'suggested_question', label: 'How to ask', icon: MessageSquareText, content: field.suggested_question, color: 'text-app-focus' },
    { key: 'help_context', label: 'Context for this deal', icon: Sparkles, content: field.help_context, color: 'text-app-critical' },
  ].filter(s => s.content);

  if (sections.length === 0) return null;

  return (
    <div className="mb-2 rounded-lg border border-app-focus bg-app-focus/10 overflow-hidden">
      {sections.map((section, i) => {
        const isOpen = expandedSection === section.key;
        const Icon = section.icon;
        return (
          <div key={section.key} className={i > 0 ? 'border-t border-app-focus/60' : ''}>
            <button
              onClick={() => setExpandedSection(isOpen ? null : section.key)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-app-focus/10 transition-colors"
            >
              <Icon className={`w-3.5 h-3.5 ${section.color} flex-shrink-0`} />
              <span className="text-xs font-medium text-app-ink flex-1">{section.label}</span>
              {isOpen ? <ChevronDown className="w-3 h-3 text-app-muted" /> : <ChevronRight className="w-3 h-3 text-app-muted" />}
            </button>
            {isOpen && (
              <div className="px-3 pb-2.5">
                {section.key === 'suggested_question' ? (
                  <div className="bg-app-surface rounded-md border border-app-focus/30 p-2.5">
                    <p className="text-xs text-app-focus italic leading-relaxed">"{section.content}"</p>
                  </div>
                ) : section.key === 'help_context' ? (
                  <div className="bg-app-surface rounded-md border border-app-critical p-2.5">
                    <p className="text-xs text-app-critical leading-relaxed">{section.content}</p>
                  </div>
                ) : (
                  <p className="text-xs text-app-muted leading-relaxed pl-5.5">{section.content}</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
